<template>
    <div class="customer-list-order-form-page-1">
        <div class="form-group row">
            <label class="col-md-2 col-form-label" for="customer">{{ _.capitalize(customerLabel) }}</label>
            <div v-if="isEditingRecurrentOrder" class="col-md-5">
                <input type="text" class="form-control-plaintext" id="store" :value="lSelectedCustomer.name" readonly />
            </div>
            <div v-else class="col-md-5">
                <v-select
                    v-dusk="'customer-select'"
                    id="customer"
                    v-model="lSelectedCustomer"
                    :options="customers"
                    :input-id="'customerSearch'"
                    label="name"
                    :disabled="!allowEditCustomer"
                >
                    <div slot="no-options">Aucun {{ customerLabel }} ne correspond à votre recherche</div>
                </v-select>
            </div>
            <div class="col-md-5">
                <a href="https://www.sellsy.com/thirds" target="_blank" class="btn btn-outline-success">
                    <i class="fad fa-external-link"></i> Créer un nouveau client (dans l'ERP)
                </a>
            </div>
        </div>

        <div v-if="lSelectedCustomer" class="form-group row">
            <div class="col-md-4 offset-md-2">
                <b>Jours de livraison :</b> {{ lSelectedCustomer.delivery_days.join(', ') }}<br>
                <b>Jour de fin de préparation :</b> {{ lSelectedCustomer.end_preparation_day }}<br>
                <b>Jour de livraison :</b> J<br>
                <template v-if="lSelectedCustomer.meta.subcustomers_delivery_day">
                    <b>Jour de distribution :</b> {{ lSelectedCustomer.meta.subcustomers_delivery_day }}<br>
                </template>
                <template v-if="lSelectedCustomer.meta.channel_type">
                    <b>Type de circuit court :</b> {{ lSelectedCustomer.meta.channel_type }}<br>
                </template>
            </div>
        </div>

        <div v-if="lSelectedCustomer" class="form-group row">
            <label class="col-md-2 col-form-label" for="deliveryDate">Date de {{ isRecurrent ? 'la première ' : ''}}livraison</label>
            <div v-if="isEditingRecurrentOrder" class="col-md-10" :class="{'offset-md-2': !order }">
                <input type="text" class="form-control-plaintext" id="deliveryDateStatic" :value="formattedDeliveryDate" readonly />
            </div>
            <DeliveryDatepicker
                v-else
                ref="deliveryDatepicker"
                id="deliveryDate"
                class="delivery-date col-md-10"
                :customer="customerForDatePickers"
                :order="order"
                :can-select-red-dates="canSelectRedDates"
                :is-recurrent="isRecurrent"
                v-model="lDeliveryDate"
            />
            <div v-if="isRecurrent && lDeliveryDate" class="col-md-4 offset-md-2">
                <div class="alert alert-info">
                    Il s'agit de la date de la première livraison.<br>
                    Cette commande sera également automatiquement programmée pour une livraison tous les <span class="font-weight-bold text-underline">{{ deliveryDateDayName }}</span> suivants.<br>
                </div>
            </div>
        </div>

        <template v-if="lDeliveryDate">
            <div class="form-group row">
                <label class="col-md-2 col-form-label" for="endPreparationDate">Date de fin de préparation</label>
                <ForcePreparationDatepicker
                    ref="endPreparationDatepicker"
                    id="endPreparationDate"
                    class="col-md-10"
                    :customer="customerForDatePickers"
                    :delivery-date="lDeliveryDate"
                    :can-select-red-dates="canSelectRedDates"
                    :hide-button="isRecurrent"
                    v-model="lEndPreparationDate"
                />
            </div>
            <div class="form-group row">
                <div class="offset-md-2">
                    <CheckExistingOrder
                        v-if="lEndPreparationDate && !this.isRecurrent"
                        :customer="lSelectedCustomer"
                        :end-preparation-date="formattedLEndPreparationDateYmd"
                        :order="order"
                        :view-existing-order-href="viewExistingOrderHrefCallback"
                        :edit-existing-order-href="editExistingOrderHrefCallback"
                        @existing-order-result="cbExistingOrderResult"
                    />
                </div>
            </div>
            <div class="form-group row">
                <AlertsZone
                    v-if="warningZoneDisplayed"
                    v-dusk="'alerts-zone-warning'"
                    class="offset-md-2"
                    type="warning"
                    title="Attention : validation requise"
                    :messages="warningMessages"
                />
            </div>
            <div class="form-group row">
                <AlertsZone
                    v-if="dangerZoneDisplayed"
                    v-dusk="'alerts-zone-danger'"
                    class="offset-md-2"
                    type="danger"
                    title="Attention : validation requise"
                    :messages="dangerMessages"
                />
            </div>

            <WarningExistingOrdersNotModified
                v-if="isRecurrent && lDeliveryDate"
                side="employees"
                :editing="!!order"
                :customer="selectedCustomer"
                :delivery-date="lDeliveryDate.date"
                class="offset-md-2"
            >
            </WarningExistingOrdersNotModified>
        </template>

        <hr>

        <div class="row">
            <div class="col-md-12">
                <a v-if="backUrl" class="btn btn-outline-dark float-left" :href="backUrl">
                    <i class="fas fa-chevron-left"></i> Quitter la prise de commande
                </a>

                <template v-if="lEndPreparationDate">
                    <template v-if="needAuthorizationToken && !authorizationToken">
                        <AuthorizationModal
                            :token-type="authorizationTokenType"
                            :token-payload="authorizationTokenPayload"
                            @authorised="onAuthorizationModalAuthorize"
                        >
                            <template #button="sp">
                                <AuthorizationModalButton
                                    :class="dangerZoneDisplayed ? 'btn-danger' : 'btn-warning'"
                                    :disabled="!canClickNext"
                                    @click="sp.showModal()"
                                >
                                    Suivant
                                </AuthorizationModalButton>
                            </template>
                            <template #messages>
                                <AlertsZone
                                    v-if="warningZoneDisplayed"
                                    type="warning"
                                    title="Attention : validation requise"
                                    :messages="warningMessages"
                                />
                                <AlertsZone
                                    v-if="dangerZoneDisplayed"
                                    type="danger"
                                    title="Attention : validation requise"
                                    :messages="dangerMessages"
                                />
                            </template>
                        </AuthorizationModal>
                    </template>
                    <button v-else type="button" class="btn btn-success float-right" @click="onClickNext" :disabled="!canClickNextGreen">
                        <i class="fas fa-chevron-right"></i> Suivant
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import vSelect from 'vue-select';

    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faAddressCard, faLockAlt} from '@fortawesome/pro-duotone-svg-icons';
    import {faCrown} from '@fortawesome/pro-solid-svg-icons';

    import {momentDay, momentDayName, momentDaysAreSame} from "../../moment-with-helpers";

    import {
        dangerZoneDisplayed,
        orderDatesDangerMessages,
        orderDatesWarningMessages,
        warningZoneDisplayed
    } from "../compositions/OrderWarnings"

    import AlertsZone from "../AlertsZone"
    import AuthorizationModal from "../AuthorizationModal";
    import DeliveryDatepicker from "../DeliveryDatepicker";
    import ForcePreparationDatepicker from "../ForcePreparationDatepicker";
    import CheckExistingOrder from "../CheckExistingOrder"
    import WarningExistingOrdersNotModified from "../WarningExistingOrdersNotModified"
    import AuthorizationModalButton from "./../AuthorizationModalButton"

    export default {
        components: {
            AuthorizationModalButton,
            AlertsZone,
            WarningExistingOrdersNotModified,
            'v-select': vSelect,
            AuthorizationModal,
            DeliveryDatepicker,
            ForcePreparationDatepicker,
            CheckExistingOrder,
        },

        props: {
            order: {
                type: Object,
                required: false,
            },
            customers: {
                type: Array,
                required: true,
            },
            customerLabel: {
                type: String,
                required: true,
            },
            selectedCustomer: {
                type: Object,
                required: false,
            },
            deliveryDate: {
                type: Object,
                required: false,
            },
            endPreparationDate: {
                type: Object,
                required: false,
            },
            allowEditCustomer: {
                type: Boolean,
                required: false,
                default: true,
            },
            canSelectRedDates: {
                type: Boolean,
                required: true,
            },
            isRecurrent: {
                type: Boolean,
                required: false,
                default: false,
            },
            authorizationTokenType: {
                type: String,
                required: true,
            },
            backUrl: {
                type: String,
                required: false,
            },
        },

        data() {
            return {
                authorizationToken: null,
                existingOrderResult: null,

                lSelectedCustomer: this.selectedCustomer,
                lDeliveryDate: this.deliveryDate,
                lEndPreparationDate: this.endPreparationDate,
            };
        },

        created() {
            library.add(faAddressCard, faCrown, faLockAlt);
        },

        methods: {
            onAuthorizationModalAuthorize(token) {
                this.authorizationToken = token;

                this.onClickNext();
            },

            onClickNext() {
                this.$emit('submit', {
                    selectedCustomer: this.lSelectedCustomer,
                    deliveryDate: this.lDeliveryDate,
                    endPreparationDate: this.lEndPreparationDate,
                    authorizationToken: this.authorizationToken,
                });
            },

            viewExistingOrderHrefCallback(order) {
                return '/espace-employes/commandes/preparation/commande/' + order.id;
            },

            editExistingOrderHrefCallback(order) {
                if (this.lSelectedCustomer) {
                    if (this.lSelectedCustomer.type === 'store') {
                        return '/espace-employes/commandes/commande-magasin/' + order.id + '/edition'
                    } else if (this.lSelectedCustomer.type === 'pickup_point') {
                        return '/espace-employes/commandes/commande-production-point-retrait/' + order.id + '/edition'
                    } else if (this.lSelectedCustomer.type === 'short_channel') {
                        return '/espace-employes/commandes/commande-circuit-court/' + order.id + '/edition'
                    }
                }
            },

            cbExistingOrderResult(result) {
                this.existingOrderResult = result;
            },
        },

        watch: {
            lSelectedCustomer(newValue, oldValue) {
                this.lDeliveryDate = null;
                this.lEndPreparationDate = null;
                this.existingOrderResult = null;
            },

            lDeliveryDate(newDate, oldDate) {
                if (!oldDate || !newDate || !momentDaysAreSame(oldDate.date, newDate.date)) {
                    this.authorizationToken = null;
                    this.lEndPreparationDate = null;
                    this.existingOrderResult = null;
                }
            },

            lEndPreparationDate(newDate, oldDate) {
                if (!oldDate || !newDate || !momentDaysAreSame(oldDate.date, newDate.date)) {
                    this.authorizationToken = null;
                    this.existingOrderResult = null;
                }
            },
        },

        computed: {
            isEditingRecurrentOrder() {
                return this.isRecurrent && this.order;
            },

            customerForDatePickers() {
                return { type: this.lSelectedCustomer.type, id: this.lSelectedCustomer.id };
            },

            hasNoExistingOrder() {
                return this.existingOrderResult != null && this.existingOrderResult.order === null;
            },

            canClickNext() {
                return !!this.lSelectedCustomer && !!this.lDeliveryDate && !!this.lEndPreparationDate && (this.hasNoExistingOrder || this.isRecurrent);
            },

            canClickNextGreen() {
                return this.canClickNext && !this.needAuthorizationToken;
            },

            deliveryDateDayName() {
                return momentDayName(this.lDeliveryDate.date);
            },

            endPreparationDatePassed() {
                return this.lEndPreparationDate && momentDay(this.lEndPreparationDate.date).isBefore(momentDay());
            },

            deliveryDatePassed() {
                return this.lDeliveryDate && momentDay(this.lDeliveryDate.date).isBefore(momentDay());
            },

            deliveryDateIsNotUsual() {
                if (this.lDeliveryDate) {
                    return this.lDeliveryDate.higherPermission === 'restricted';
                }
                return false;
            },

            deliveryDateIsForbidden() {
                if (this.lDeliveryDate) {
                    return this.lDeliveryDate.higherPermission === 'forbidden';
                }
                return false;
            },

            endPreparationDateForced() {
                if (this.lEndPreparationDate) {
                    return this.lEndPreparationDate.type === 'forced';
                }
                return false;
            },

            warningMessages() {
                return orderDatesWarningMessages(this.lDeliveryDate)
            },

            dangerMessages() {
                return orderDatesDangerMessages(this.lDeliveryDate, this.lEndPreparationDate)
            },

            warningZoneDisplayed,

            dangerZoneDisplayed,

            needAuthorizationToken() {
                return !this.authorizationToken && (this.dangerZoneDisplayed || this.warningZoneDisplayed);
            },

            authorizationTokenPayload() {
                return {
                    order_id: (this.order && this.order.id) || null,
                    token_type: this.authorizationTokenType,
                    customer_type: this.lSelectedCustomer.type,
                    customer_id: this.lSelectedCustomer.id,
                    delivery_date: momentDay(this.lDeliveryDate.date).format('YYYY-MM-DD'),
                    end_preparation_date: momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD'),
                };
            },

            formattedLEndPreparationDateYmd() {
                return momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD');
            },

            formattedDeliveryDate() {
                if (this.lDeliveryDate) {
                    return momentDay(this.lDeliveryDate.date).format('dddd Do MMMM YYYY');
                }
            },
        }
    }
</script>

<style lang="scss">
    .customer-list-order-form-page-1 {
        .btn-social > :first-child {
            height: inherit;
        }
    }
</style>
